import React, { useCallback } from 'react';
import { Col, DatePicker, Descriptions, Form, Row, TimePicker } from 'antd';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { ValidTime } from '../tab-pane/Details';
import { DEFAULT_TIME, FORMAT_DATE } from '../../../../constants/common';
import { TOUR_TRASH_TRANSLATION } from '../../../../constants/define/translation';

interface DateTimePickerSectionProps {
  form: any;
  startTime: any;
  endTime: any;
  onChangeStartTime: (value: moment.Moment | null) => void;
  onChangeEndTime: (value: moment.Moment | null) => void;
  onHandleChangeDateStart: (val: any) => void;
  onHandleChangeDateEnd: (val: any) => void;
  validTime: ValidTime;
  isHandleViewTourTrash: boolean;
}

const getDisableHour = (
  date: moment.Moment,
  dateCheck: moment.Moment | Date,
  timeCheck: moment.Moment | Date,
  isStart: boolean,
): number[] => {
  const disableTimesToHourNow: any[] = [];
  const disableTimesToHourDateCheck: any[] = [];
  if (moment(date).day() === moment().day()) {
    for (let i = 0; i < moment().hour(); i += 1) {
      disableTimesToHourNow.push(i);
    }
  }

  if (moment(date).day() === moment(dateCheck).day()) {
    if (isStart) {
      for (let i = moment(timeCheck).hour() + 1; i < 24; i += 1) {
        disableTimesToHourDateCheck.push(i);
      }
    } else {
      for (let i = 0; i < moment(timeCheck).hour(); i += 1) {
        disableTimesToHourDateCheck.push(i);
      }
    }
  }

  return Array.from(new Set([...disableTimesToHourNow, ...disableTimesToHourDateCheck]));
};

export const DateTimePickerSection = (props: DateTimePickerSectionProps) => {
  const { t } = useTranslation(TOUR_TRASH_TRANSLATION);
  const {
    form,
    startTime,
    endTime,
    onChangeStartTime,
    onChangeEndTime,
    onHandleChangeDateStart,
    onHandleChangeDateEnd,
    validTime,
    isHandleViewTourTrash,
  } = props;

  const { validateStatus, help, isCheckStartDate } = validTime;

  const disabledStartDate = useCallback(
    date => {
      const endDate = form.getFieldValue('endDate');
      const now = moment().format(FORMAT_DATE);
      const isBeforeNow = moment(date).diff(now, 'second') < 0;
      const isAfterEndDate = endDate && moment(endDate).diff(moment(date), 'second') < 0;

      return isBeforeNow || isAfterEndDate;
    },
    [form],
  );

  const disabledEndDate = useCallback(
    date => {
      const startDate = form.getFieldValue('startDate');
      const now = moment().format(FORMAT_DATE);
      const isBeforeNow = moment(date).diff(now, 'second') < 0;
      const isBeforeStartDate = startDate && moment(date).diff(moment(startDate).format(FORMAT_DATE), 'second') < 0;

      return isBeforeNow || isBeforeStartDate;
    },
    [form],
  );

  return (
    <>
      <Descriptions title={t('tab.detail.form.time.title')} />
      <Row className="row-datetime-picker">
        <Col span={11}>
          <Form.Item
            name="startTime"
            rules={[{ required: true, message: t('tab.detail.form.validate.startTimeBlank') }]}
            validateStatus={isCheckStartDate ? validateStatus : undefined}
            help={isCheckStartDate ? help : undefined}
            initialValue={DEFAULT_TIME}
          >
            <TimePicker
              placeholder={t('tab.detail.form.time.startTimePlaceholder')}
              use12Hours
              format="hh:mm A"
              value={startTime}
              onChange={onChangeStartTime}
              disabledHours={() =>
                getDisableHour(form.getFieldValue('startDate'), form.getFieldValue('endDate'), endTime, true)
              }
              showNow={false}
              inputReadOnly={isHandleViewTourTrash}
              allowClear={!isHandleViewTourTrash}
              disabled={isHandleViewTourTrash}
              minuteStep={15}
            />
          </Form.Item>
        </Col>
        <Col span={11} offset={2}>
          <Form.Item
            name="startDate"
            rules={[{ required: true, message: t('tab.detail.form.validate.startDateBlank') }]}
            validateStatus={isCheckStartDate ? validateStatus : undefined}
          >
            <DatePicker
              placeholder={t('tab.detail.form.time.startDatePlaceholder')}
              format="ll"
              disabledDate={disabledStartDate}
              onChange={onHandleChangeDateStart}
              inputReadOnly={isHandleViewTourTrash}
              allowClear={!isHandleViewTourTrash}
              disabled={isHandleViewTourTrash}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={11}>
          <Form.Item
            name="endTime"
            rules={[{ required: true, message: t('tab.detail.form.validate.endTimeBlank') }]}
            validateStatus={!isCheckStartDate ? validateStatus : undefined}
            help={!isCheckStartDate ? help : undefined}
            initialValue={DEFAULT_TIME}
          >
            <TimePicker
              placeholder={t('tab.detail.form.time.endDatePlaceholder')}
              use12Hours
              format="hh:mm A"
              value={endTime}
              onChange={onChangeEndTime}
              disabledHours={() =>
                getDisableHour(form.getFieldValue('endDate'), form.getFieldValue('startDate'), startTime, false)
              }
              showNow={false}
              inputReadOnly={isHandleViewTourTrash}
              allowClear={!isHandleViewTourTrash}
              disabled={isHandleViewTourTrash}
              minuteStep={15}
            />
          </Form.Item>
        </Col>
        <Col span={11} offset={2}>
          <Form.Item
            name="endDate"
            rules={[{ required: true, message: t('tab.detail.form.validate.endDateBlank') }]}
            validateStatus={!isCheckStartDate ? validateStatus : undefined}
          >
            <DatePicker
              placeholder={t('tab.detail.form.time.endDatePlaceholder')}
              format="ll"
              disabledDate={disabledEndDate}
              onChange={onHandleChangeDateEnd}
              inputReadOnly={isHandleViewTourTrash}
              allowClear={!isHandleViewTourTrash}
              disabled={isHandleViewTourTrash}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};
